import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { useTranslation } from 'react-i18next';
import {
  DataGridV2,
  ProgressCell,
  type DataGridV2Column,
  type DataGridV2QuickFilter,
  type DataGridV2RowAction,
  type DataGridV2Tone,
} from '@bloomays-lib/ui.shared/datagrid';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  accountManagerInitials,
  computeRemainingAmount,
  derivePurchaseOrderDisplayStatus,
  DashboardPurchaseOrder,
  formatAccountManagerShort,
  MissionEnrichment,
  PURCHASE_ORDER_PDF_ACCEPT,
  PurchaseOrderDisplayStatus,
} from '../../helpers/purchaseOrderDashboard';
import { PURCHASE_ORDER_DISPLAY_STATUS_TONE } from '../../helpers/purchaseOrderSemanticStatus';

type ExistingRow = {
  recordId: string;
  orderNumber: string;
  referenceSubtitle: string;
  missionLabel: string;
  missionSubtitle: string;
  amShort: string;
  amInitials: string;
  amountHT?: number | null;
  invoicedAmountHT?: number | null;
  remaining?: number;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  fileUrl?: string | null;
  displayStatus: PurchaseOrderDisplayStatus;
  raw: DashboardPurchaseOrder;
};

export type ReceivedPurchaseOrderTabProps = {
  purchaseOrders: DashboardPurchaseOrder[];
  missionLabelByRecordId: Record<string, string>;
  clientByMissionRecordId: Record<string, string>;
  missionEnrichmentByRecordId?: Record<string, MissionEnrichment>;
  pendingRecordId?: string | null;
  onEdit: (purchaseOrder: DashboardPurchaseOrder) => void;
  onDelete: (purchaseOrder: DashboardPurchaseOrder) => void;
  onUpload: (purchaseOrder: DashboardPurchaseOrder, file: File) => void;
};

const formatAmount = (value: number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  return `${value.toLocaleString('fr-FR')} €`;
};

const formatDate = (value: string | Date | undefined | null): string => {
  if (!value) {
    return '—';
  }
  return convertDateToStringFormat(value as string, 'dd/MM/yyyy');
};

export const ReceivedPurchaseOrderTab = ({
  purchaseOrders,
  missionLabelByRecordId,
  clientByMissionRecordId,
  missionEnrichmentByRecordId,
  pendingRecordId,
  onEdit,
  onDelete,
  onUpload,
}: ReceivedPurchaseOrderTabProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<DashboardPurchaseOrder | null>(null);

  const statusLabel = useMemo<Record<PurchaseOrderDisplayStatus, string>>(
    () => ({
      received: t('statusReceived'),
      partial: t('statusPartial'),
      waiting: t('statusWaiting'),
      expired: t('statusExpired'),
      soldout: t('statusSoldout'),
    }),
    [t],
  );

  const rows = useMemo<ExistingRow[]>(() => {
    return purchaseOrders.map((purchaseOrder) => {
      const firstMissionRecordId = purchaseOrder.missionsRecordId?.[0];
      const enrichment = firstMissionRecordId ? missionEnrichmentByRecordId?.[firstMissionRecordId] : undefined;
      const clientName =
        enrichment?.clientSocietyName ??
        (firstMissionRecordId ? clientByMissionRecordId[firstMissionRecordId] : undefined) ??
        '—';
      const bloomerName = enrichment?.bloomerFullName;
      const accountManager = enrichment?.accountManager;
      const missionSubtitle = bloomerName ? `${clientName} — ${bloomerName}` : clientName;
      return {
        recordId: purchaseOrder.recordId,
        orderNumber: purchaseOrder.orderNumber,
        referenceSubtitle: clientName,
        missionLabel: firstMissionRecordId ? missionLabelByRecordId[firstMissionRecordId] ?? '—' : '—',
        missionSubtitle,
        amShort: formatAccountManagerShort(accountManager),
        amInitials: accountManagerInitials(accountManager),
        amountHT: purchaseOrder.amountHT,
        invoicedAmountHT: purchaseOrder.invoicedAmountHT,
        remaining: computeRemainingAmount(purchaseOrder),
        startDate: purchaseOrder.startDate,
        endDate: purchaseOrder.endDate,
        fileUrl: purchaseOrder.fileUrl,
        displayStatus: derivePurchaseOrderDisplayStatus(purchaseOrder),
        raw: purchaseOrder,
      };
    });
  }, [purchaseOrders, missionLabelByRecordId, clientByMissionRecordId, missionEnrichmentByRecordId]);

  const validityContent = (row: ExistingRow): { range: string; statusText?: string; statusTone?: DataGridV2Tone } => {
    if (!row.endDate) {
      return { range: formatDate(row.startDate) };
    }
    const days = differenceInCalendarDays(new Date(row.endDate), new Date());
    const range = `${formatDate(row.startDate)} → ${formatDate(row.endDate)}`;
    if (days < 0) {
      return { range, statusText: t('expiredAgo', { days: Math.abs(days) }), statusTone: 'danger' };
    }
    if (days <= 14) {
      return { range, statusText: t('expiresIn', { days }), statusTone: 'warning' };
    }
    return { range };
  };

  const triggerUpload = useCallback((purchaseOrder: DashboardPurchaseOrder) => {
    setUploadTarget(purchaseOrder);
    requestAnimationFrame(() => uploadInputRef.current?.click());
  }, []);

  const buildRowActions = useCallback(
    (row: ExistingRow): DataGridV2RowAction[] => {
      const actions: DataGridV2RowAction[] = [];
      if (row.fileUrl) {
        actions.push({
          label: t('viewFile'),
          icon: <OpenInNewOutlinedIcon fontSize="small" />,
          onClick: () => window.open(row.fileUrl ?? undefined, '_blank', 'noopener,noreferrer'),
        });
      } else {
        actions.push({
          label: t('uploadFile'),
          icon: <UploadFileOutlinedIcon fontSize="small" />,
          disabled: pendingRecordId === row.recordId,
          onClick: () => triggerUpload(row.raw),
        });
      }
      actions.push({
        label: t('editAction'),
        icon: <EditOutlinedIcon fontSize="small" />,
        divider: true,
        onClick: () => onEdit(row.raw),
      });
      actions.push({
        label: t('deleteConfirm'),
        icon: <DeleteOutlineIcon fontSize="small" />,
        danger: true,
        onClick: () => onDelete(row.raw),
      });
      return actions;
    },
    [onDelete, onEdit, pendingRecordId, t, triggerUpload],
  );

  const statusQuickFilter = useMemo<DataGridV2QuickFilter<ExistingRow>>(
    () => ({
      field: 'displayStatus',
      label: t('colStatus'),
      display: 'inline',
      allLabel: t('filterAll'),
      options: [
        { value: 'received', label: t('filterReceived'), tone: 'success' },
        { value: 'waiting', label: t('filterWaiting'), tone: 'info' },
        { value: 'expired', label: t('filterExpired'), tone: 'danger' },
        { value: 'soldout', label: t('filterSoldout'), tone: 'neutral' },
        { value: 'partial', label: t('filterPartial'), tone: 'warning' },
      ],
      predicate: (row, value) => row.displayStatus === value,
    }),
    [t],
  );

  const columns = useMemo<DataGridV2Column<ExistingRow>[]>(
    () => [
      {
        field: 'orderNumber',
        header: t('colReference'),
        minWidth: 160,
        searchText: (row) => `${row.orderNumber} ${row.referenceSubtitle} ${row.missionLabel} ${row.missionSubtitle}`,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.orderNumber}
            </Typography>
            <SecondaryText variant="caption">{row.referenceSubtitle}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'missionLabel',
        header: t('colMission'),
        minWidth: 240,
        flex: 1,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.missionLabel}
            </Typography>
            <SecondaryText variant="caption">{row.missionSubtitle}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'amountHT',
        header: t('colEngagedAmount'),
        subHeader: t('colEngagedAmountSub'),
        minWidth: 200,
        render: (row) => (
          <AmountStack>
            <AmountTotal variant="body2">{formatAmount(row.amountHT)}</AmountTotal>
            <ProgressCell value={row.invoicedAmountHT ?? 0} max={row.amountHT ?? 0} label=" " />
            <SecondaryText variant="caption">
              {t('remainingShort', { amount: formatAmount(row.remaining) })}
            </SecondaryText>
          </AmountStack>
        ),
      },
      {
        field: 'endDate',
        header: t('colValidity'),
        minWidth: 220,
        valueGetter: (row) => validityContent(row).range,
        render: (row) => {
          const { range, statusText, statusTone } = validityContent(row);
          return (
            <CellStack>
              <Typography variant="body2">{range}</Typography>
              {statusText ? <ValidityStatus $tone={statusTone ?? 'neutral'}>{statusText}</ValidityStatus> : null}
            </CellStack>
          );
        },
      },
      {
        field: 'displayStatus',
        header: t('colStatus'),
        minWidth: 130,
        variant: 'chip',
        chip: (row) => ({
          label: statusLabel[row.displayStatus],
          tone: PURCHASE_ORDER_DISPLAY_STATUS_TONE[row.displayStatus],
          dot: true,
        }),
      },
      {
        field: 'amShort',
        header: t('colAm'),
        width: 140,
        variant: 'avatar',
        avatar: (row) => ({
          initials: row.amInitials,
          name: row.amShort,
        }),
      },
      {
        field: 'actions',
        header: t('colActions'),
        width: 120,
        variant: 'actions',
        actions: buildRowActions,
      },
    ],
    [buildRowActions, statusLabel, t],
  );

  const handleUploadChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && uploadTarget) {
      onUpload(uploadTarget, file);
    }
    event.target.value = '';
    setUploadTarget(null);
  };

  if (rows.length === 0) {
    return <EmptyText variant="body1Light">{t('emptyExisting')}</EmptyText>;
  }

  return (
    <Box data-testid="purchase-order-existing-grid">
      <input ref={uploadInputRef} type="file" accept={PURCHASE_ORDER_PDF_ACCEPT} hidden onChange={handleUploadChange} />
      <DataGridV2<ExistingRow>
        uniqueId="purchase-orders-existing-v2"
        rows={rows}
        columns={columns}
        getRowId={(row) => row.recordId}
        quickFilters={[statusQuickFilter]}
        search
        searchPlaceholder={t('searchExistingPlaceholder')}
        pagination
        pageSize={25}
        getRowTone={(row) => (row.displayStatus === 'expired' ? 'danger' : undefined)}
      />
    </Box>
  );
};

export default ReceivedPurchaseOrderTab;

const CellStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
}));

const AmountStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',
}));

const AmountTotal = styled(Typography)(() => ({
  fontWeight: 700,
}));

const ValidityStatus = styled('span')<{ $tone: DataGridV2Tone }>(({ $tone }) => {
  const colors: Record<DataGridV2Tone, string> = {
    neutral: '#6B6B7E',
    info: '#1449B8',
    success: '#4D7236',
    warning: '#C17A00',
    danger: '#FF3328',
    purple: '#5558D4',
  };
  return {
    fontSize: 11,
    fontWeight: 600,
    color: colors[$tone],
  };
});

const SecondaryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#374151',
  fontStyle: 'italic',
  marginTop: 16,
}));
